/**
 * Utility functions for localStorage management
 * Provides wrapper functions to handle JSON serialization/deserialization
 * and error handling when working with localStorage in the browser
 */

/**
 * Check if localStorage is available in the browser
 * @returns {boolean} Whether localStorage is available
 * @private
 */
const isLocalStorageAvailable = () => {
  try {
    const testKey = '__test_localStorage__';
    localStorage.setItem(testKey, testKey);
    localStorage.removeItem(testKey);
    return true;
  } catch (e) {
    console.error('localStorage is not available:', e);
    return false;
  }
};

/**
 * Get an item from localStorage
 * @param {string} key - Key to retrieve
 * @param {*} [defaultValue=null] - Default value if key not found or parsing fails
 * @returns {*} Retrieved value or defaultValue
 */
export const getItem = (key, defaultValue = null) => {
  if (!isLocalStorageAvailable()) return defaultValue;
  
  try {
    const item = localStorage.getItem(key);
    
    if (item === null) {
      return defaultValue;
    }
    
    return JSON.parse(item);
  } catch (error) {
    console.error(`Error getting item ${key} from localStorage:`, error);
    return defaultValue;
  }
};

/**
 * Set an item in localStorage
 * @param {string} key - Key to set
 * @param {*} value - Value to store (will be JSON stringified)
 * @returns {boolean} Whether operation was successful
 */
export const setItem = (key, value) => {
  if (!isLocalStorageAvailable()) return false;
  
  try {
    localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch (error) {
    console.error(`Error setting item ${key} in localStorage:`, error);
    return false;
  }
};

/**
 * Check if an item exists in localStorage
 * @param {string} key - Key to check
 * @returns {boolean} Whether the key exists
 */
export const hasItem = (key) => {
  if (!isLocalStorageAvailable()) return false;
  return localStorage.getItem(key) !== null;
};

/**
 * Remove an item from localStorage
 * @param {string} key - Key to remove
 * @returns {boolean} Whether operation was successful
 */
export const removeItem = (key) => {
  if (!isLocalStorageAvailable()) return false;
  
  try {
    localStorage.removeItem(key);
    return true;
  } catch (error) {
    console.error(`Error removing item ${key} from localStorage:`, error);
    return false;
  }
};

/**
 * Clear all items from localStorage
 * @returns {boolean} Whether operation was successful
 */
export const clear = () => {
  if (!isLocalStorageAvailable()) return false;
  
  try {
    localStorage.clear();
    return true;
  } catch (error) {
    console.error('Error clearing localStorage:', error);
    return false;
  }
};

/**
 * Get all keys in localStorage
 * @returns {Array} Array of keys
 */
export const getKeys = () => {
  if (!isLocalStorageAvailable()) return [];
  
  try {
    const keys = [];
    for (let i = 0; i < localStorage.length; i++) {
      keys.push(localStorage.key(i));
    }
    return keys;
  } catch (error) {
    console.error('Error getting localStorage keys:', error);
    return [];
  }
};

/**
 * Get localStorage size and usage info
 * @returns {Object} Storage info object
 */
export const getStorageInfo = () => {
  if (!isLocalStorageAvailable()) {
    return { available: false, error: 'localStorage not available' };
  }
  
  try {
    let totalSize = 0;
    let keyCount = 0;
    const items = {};
    
    // Calculate size of all items
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      const value = localStorage.getItem(key);
      const size = (key.length + value.length) * 2; // UTF-16 uses 2 bytes per character
      
      items[key] = {
        size,
        sizeFormatted: `${(size / 1024).toFixed(2)} KB`
      };
      
      totalSize += size;
      keyCount++;
    }
    
    return {
      available: true,
      totalSize,
      totalSizeFormatted: `${(totalSize / 1024).toFixed(2)} KB`,
      keyCount,
      items,
      estimatedMaxSize: 5 * 1024 * 1024, // Estimate: 5MB
      percentUsed: (totalSize / (5 * 1024 * 1024) * 100).toFixed(2)
    };
  } catch (error) {
    console.error('Error calculating localStorage size:', error);
    return { 
      available: true,
      error: error.message,
      totalSize: 0,
      keyCount: 0
    };
  }
};

/**
 * Import data from JSON to localStorage
 * @param {Object} data - JSON data with key-value pairs to import
 * @param {boolean} [overwrite=false] - Whether to overwrite existing keys
 * @returns {Object} Import result with counts
 */
export const importData = (data, overwrite = false) => {
  if (!isLocalStorageAvailable()) {
    return { success: false, error: 'localStorage not available' };
  }
  
  try {
    const result = {
      total: 0,
      imported: 0,
      skipped: 0,
      errors: 0
    };
    
    Object.keys(data).forEach(key => {
      result.total++;
      
      if (!overwrite && hasItem(key)) {
        result.skipped++;
        return;
      }
      
      try {
        setItem(key, data[key]);
        result.imported++;
      } catch (error) {
        console.error(`Error importing key ${key}:`, error);
        result.errors++;
      }
    });
    
    return {
      success: result.errors === 0,
      ...result
    };
  } catch (error) {
    console.error('Error importing data to localStorage:', error);
    return { 
      success: false,
      error: error.message
    };
  }
};

/**
 * Export all localStorage data
 * @returns {Object} Exported data
 */
export const exportData = () => {
  if (!isLocalStorageAvailable()) {
    return { success: false, error: 'localStorage not available' };
  }
  
  try {
    const data = {};
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      data[key] = getItem(key);
    }
    
    return {
      success: true,
      data,
      timestamp: new Date().toISOString()
    };
  } catch (error) {
    console.error('Error exporting localStorage data:', error);
    return { 
      success: false,
      error: error.message
    };
  }
};